// MorningDigest (TASK-611) — the overnight signal × holdings × client-constraints
// triage on the Today surface. At most five COMPUTED items (the LLM wrote only the
// one-line summaries); every item carries its source chip, affected clients with
// CHF, KG badges and a "Why?" provenance line. Honest states (TASK-855): quiet night says so
// WITH the scanned count, so "quiet" is distinguishable from "never looked"; a >26h-old
// digest shows a stale banner; and items left over from an earlier run say which run they
// came from, because `stale` is an age test on the RUN and stays false when the digest ran
// on time and found nothing. Three different facts, three different lines — never presented
// as fresh.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../../../lib/httpClient';
import { useCanvasStore } from '../../../stores/canvasStore';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { SourceCard, useLinkPreviews, type LinkPreview } from '../../ui/SourceCard';
import { RadarTickHealth } from './RadarTickHealth';

const CHF = new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 });

interface DigestItem {
  id: number;
  rank: number;
  kind: 'news' | 'research';
  summary: string;
  suggested_action: string | null;
  match_reason: string;
  source: {
    title: string;
    origin: string;
    issuer: string | null;
    url: string | null;
    page: number | null;
    published_at: string | null;
    subject?: string | null;
  };
  clients: { client_id: string; name: string; exposure_chf: number | null }[];
  kg_hits: string[];
  total_exposure_chf: number | null;
}

interface DigestResponse {
  items: DigestItem[];
  digest_date: string | null;
  generated_at: string | null;
  sources_scanned: number | null;
  /*
    TASK-855 / DEC-1: distinct from `stale`, and deliberately so. `stale` means the digest did
    NOT run. This means it ran, found nothing, and the items below are from an earlier run —
    which is why `stale` is correctly false over a nineteen-day-old item. Two facts, two
    fields; folding them together would lose the one an advisor needs at 7am.
  */
  items_predate_last_run?: boolean;
  stale: boolean;
}

const KIND_ICON: Record<string, string> = { news: '📰', research: '📄' };

function DigestRow({ item, preview }: { item: DigestItem; preview?: LinkPreview }) {
  const [showWhy, setShowWhy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  const subject = item.source.subject ?? null;

  if (dismissed) return null;
  const sourceDate = item.source.published_at?.slice(0, 10);

  return (
    <div className="border-b border-border py-2 last:border-b-0" data-testid="digest-item">
      <div className="flex items-start gap-2 text-[13px]">
        <span aria-hidden>{KIND_ICON[item.kind] ?? '•'}</span>
        <div className="min-w-0 flex-1">
          <p className="text-foreground">{item.summary}</p>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            [{item.source.issuer ?? item.source.origin}
            {sourceDate ? ` · ${sourceDate}` : ''}
            {item.source.page ? ` · p.${item.source.page}` : ''}]
            {item.clients.length > 0 && (
              <span>
                {' '}
                · {item.clients.length} client{item.clients.length > 1 ? 's' : ''}
                {item.total_exposure_chf != null && ` · CHF ${CHF.format(item.total_exposure_chf)}`}
                {' — '}
                {item.clients
                  .map((c) => `${c.name}${c.exposure_chf != null ? ` CHF ${CHF.format(c.exposure_chf)}` : ''}`)
                  .join(', ')}
              </span>
            )}
          </div>
          {item.kg_hits.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {item.kg_hits.slice(0, 3).map((hit) => (
                <span
                  key={hit}
                  className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                  title={hit}
                >
                  ⚠ {hit.length > 48 ? `${hit.slice(0, 48)}…` : hit}
                </span>
              ))}
            </div>
          )}
          {/* TASK-690 (review fix): the source card was built and never rendered — every
              digest item still showed a bare bracketed publisher. Rendered only when the
              item HAS a url; the card itself falls back to the publisher domain when the
              preview could not be fetched, and never invents a headline. */}
          {item.source.url && (
            <SourceCard
              url={item.source.url}
              preview={preview}
              fallbackTitle={item.source.issuer ?? undefined}
              className="mt-1.5"
            />
          )}
          <div className="mt-1 text-[11px] italic text-muted-foreground">
            {item.suggested_action ?? 'No action suggested'}
          </div>
          {showWhy && (
            <div className="mt-1 rounded bg-accent/60 px-2 py-1 text-[11px] text-muted-foreground" data-testid="digest-why">
              Selected because: {item.match_reason}
            </div>
          )}
          <div className="mt-1 flex gap-3 text-[11px]">
            {subject && (
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => addSpecs([{ component: 'ResearchBrief', props: { subject } }])}
              >
                Open dossier
              </button>
            )}
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => setShowWhy((v) => !v)}
            >
              Why this?
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => {
                setDismissed(true);
                void httpClient.patch(`/api/digest/items/${item.id}/dismiss`).catch(() => setDismissed(false));
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MorningDigest() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery<DigestResponse>({
    queryKey: ['morning-digest'],
    queryFn: () => httpClient.get<DigestResponse>('/api/digest'),
    staleTime: 10 * 60 * 1000,
  });

  const items = data?.items ?? [];
  // One batched unfurl for the whole digest (the endpoint caches server-side), so five items
  // cost one request rather than five. A failed batch leaves every card on its publisher
  // domain — the honest fallback — instead of blocking the digest.
  const previewsQuery = useLinkPreviews(items.map((i) => i.source.url ?? '').filter(Boolean));
  const previewByUrl = new Map(
    (previewsQuery.data?.previews ?? []).map((p) => [p.url, p]),
  );

  return (
    <WidgetContainer
      title="☕ Morning Book Digest"
      /*
        TASK-825: this is THE tile the defect was observed on — "as of 07:03 AM" against a
        browser clock of 07:03:48, over content dated 2026-08-07, eighteen days old. The
        response has carried `generated_at` all along; the stamp simply never read it.
        `digest_date` is the day the digest is ABOUT, so `generated_at` is the right one:
        it is when this content was actually produced.
      */
      dataAsOf={data?.generated_at ?? data?.digest_date ?? null}
      source={
        data?.digest_date
          ? `${data.digest_date}${data.sources_scanned != null ? ` · ${items.length} item${items.length === 1 ? '' : 's'} from ${data.sources_scanned} signals` : ''}`
          : 'Overnight triage'
      }
    >
      {isLoading && <div className="h-16 w-full animate-pulse rounded bg-accent" />}
      {isError && (
        <WidgetErrorState
          title="Could not load the morning digest"
          onRetry={() => {
            void queryClient.invalidateQueries({ queryKey: ['morning-digest'] });
            void refetch();
          }}
        />
      )}
      {!isLoading && !isError && data?.stale && (
        <div
          className="mb-2 rounded border border-amber-300/60 bg-amber-50 px-2 py-1 text-[12px] text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
          data-testid="digest-stale"
        >
          The digest didn’t run last night — items below may be outdated.
        </div>
      )}
      {/*
        TASK-855: NOT the stale banner above — that says the digest did not run, which is a
        different claim and is still made only when it is true. This one says the most recent
        run found nothing, so what follows predates it. The items are still shown: DEC-1
        rejected hiding them, because an advisor may not have acted on them yet.

        Review: the copy said "Last night's run" and the field asserts no such thing — it says
        the LATEST run, of any age. Shown beside the stale banner (both can be true when the
        chain died days ago) that read "the digest didn't run last night" directly above "last
        night's run found nothing", which is a contradiction the widget stated by itself. It
        names both dates instead, from values the response already carries.
      */}
      {!isLoading && !isError && items.length > 0 && data?.items_predate_last_run && (
        <div
          className="mb-2 rounded border border-border bg-muted/60 px-2 py-1 text-[12px] text-muted-foreground"
          data-testid="digest-predates-run"
        >
          The last run{data.generated_at ? ` (${data.generated_at.slice(0, 10)})` : ''} found
          nothing new — showing items from {data.digest_date}.
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <div className="text-[13px] text-muted-foreground" data-testid="digest-quiet">
          Quiet night — 0 items affecting your book
          {data?.sources_scanned != null ? ` (${data.sources_scanned} signals scanned)` : ''}.
        </div>
      )}
      {!isLoading &&
        !isError &&
        items.map((item) => (
          <DigestRow
            key={item.id}
            item={item}
            preview={item.source.url ? previewByUrl.get(item.source.url) : undefined}
          />
        ))}
      {/*
        TASK-921: the fourth honest state, and the one the three above could not express.
        `stale`, `items_predate_last_run` and "quiet night" all describe the DIGEST. None of
        them can say the Change Radar that feeds it has been raising for every account since
        04:00 — and "Quiet night — 0 items affecting your book" is precisely the sentence a
        dead sweep produces. Always rendered, because a healthy line is what makes the
        failing one mean something.
      */}
      {!isLoading && !isError && (
        <div className="mt-2 border-t border-border pt-2">
          <RadarTickHealth />
        </div>
      )}
    </WidgetContainer>
  );
}
